import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Icon from "../Icon";
import { Avatar, Badge } from "../Atoms";
import { ST_CLR, PR_CLR } from "../../constants";
import { fmtHours } from "../../utils";
import {
  PX_DIA, ROW_H, ESCALAS, rangoDe, marcasRegla, bandasNoLaborables,
  barraDe, conectorDe, xDe, hoyISO, diasEntre, escalaQueCabe, alcanzaElMinimo,
} from "./timelineScale";

const CLAVE_ESCALA = "alm.cron.escala";
const ANCHO_NOMBRES = 220;
const MAX_CONECTORES = 60;

// Cronologia del proyecto: un Gantt a mano.
//
// La columna de nombres va en HTML y la rejilla en un solo <svg>. El texto largo
// necesita text-overflow:ellipsis, y en SVG eso obliga a medir con
// getComputedTextLength: en HTML es una linea de CSS.
export default function CronologiaView({
  proyecto, tareas = [], dependencias = [], festivos = {},
  minutosPorTarea = {}, porDiaPorTarea = {}, tiempoParcial, tiempoError,
  onAbrirPaso,
}) {
  const [escala, setEscala] = useState(() => {
    try { return localStorage.getItem(CLAVE_ESCALA) || "dia"; } catch { return "dia"; }
  });
  const [filaActiva, setFilaActiva] = useState(null);
  const cuerpoRef = useRef(null);
  const reglaRef  = useRef(null);
  const [anchoVis, setAnchoVis] = useState(800);

  const px    = PX_DIA[escala];
  const altoF = ROW_H[escala];
  const hoy   = hoyISO();

  const { iso0, iso1 } = useMemo(() => rangoDe(proyecto, tareas, hoy), [proyecto, tareas, hoy]);
  const regla  = useMemo(() => marcasRegla(iso0, iso1, escala, px), [iso0, iso1, escala, px]);
  const bandas = useMemo(() => bandasNoLaborables(iso0, iso1, festivos, px), [iso0, iso1, festivos, px]);

  // Los hitos se fijan arriba: son las referencias del proyecto. Y lo que no
  // tiene fechas no puede tener barra, asi que va a su propio grupo al final.
  const { hitos, conFechas, sinFechas } = useMemo(() => {
    const h = [], c = [], s = [];
    for (const t of tareas) {
      // Inicio y Fin ya no tienen fechas propias —son los extremos del flujo y
      // las suyas son las del proyecto—, pero los proyectos viejos conservan
      // guardado lo que se les puso cuando si tenian campos. Pintarlo seria una
      // barra de un dia en medio de los pasos que contradice a la cabecera.
      const delimitador = t.kind === "inicio" || t.kind === "fin";
      const tieneFechas = !delimitador && !!(t.start_date || t.due_date);
      if (!tieneFechas) { s.push(t); continue; }
      if (t.kind === "hito") h.push(t); else c.push(t);
    }
    const porFecha = (a, b) => String(a.start_date || a.due_date || "")
      .localeCompare(String(b.start_date || b.due_date || ""));
    return { hitos: h.sort(porFecha), conFechas: c.sort(porFecha), sinFechas: s };
  }, [tareas]);

  const filas = useMemo(() => [...hitos, ...conFechas], [hitos, conFechas]);
  const indiceFila = useMemo(() => {
    const m = {};
    filas.forEach((t, i) => { m[String(t.id)] = i; });
    return m;
  }, [filas]);

  const barras = useMemo(() => {
    const m = {};
    for (const t of filas) m[String(t.id)] = barraDe(t, iso0, px, minutosPorTarea[String(t.id)], hoy);
    return m;
  }, [filas, iso0, px, minutosPorTarea, hoy]);

  // Conectores: solo los que tienen fechas en los DOS extremos. Con muchos se
  // esconden salvo el de la fila activa, o el Gantt se vuelve ilegible.
  const conectores = useMemo(() => {
    const out = [];
    for (const d of dependencias) {
      const a = barras[String(d.from_task_id)], b = barras[String(d.to_task_id)];
      const fa = indiceFila[String(d.from_task_id)], fb = indiceFila[String(d.to_task_id)];
      if (!a || !b || fa === undefined || fb === undefined) continue;
      const c = conectorDe(a, b, fa, fb, altoF);
      if (!c) continue;
      // Violacion de dependencia: el sucesor empieza antes de que acabe el
      // predecesor. Esto es el valor real de tener el flujo y la cronologia
      // sobre el mismo modelo, y sale gratis.
      const mal = diasEntre(a.fin, b.inicio) < 0;
      out.push({ ...c, id: d.id, mal, from: String(d.from_task_id), to: String(d.to_task_id) });
    }
    return out;
  }, [dependencias, barras, indiceFila, altoF]);

  const demasiados = conectores.length > MAX_CONECTORES;

  // Scroll compartido entre la regla y el cuerpo
  const alScroll = e => {
    if (reglaRef.current) reglaRef.current.scrollLeft = e.currentTarget.scrollLeft;
  };

  useLayoutEffect(() => {
    const el = cuerpoRef.current;
    if (!el) return;
    const medir = () => setAnchoVis(el.clientWidth || 800);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Al cambiar de escala se conserva la fecha del centro de la vista. Sin esto,
  // pasar de "dia" a "mes" te teletransporta a otro año.
  const escalaPrev = useRef(escala);
  useEffect(() => {
    const el = cuerpoRef.current;
    if (!el || escalaPrev.current === escala) return;
    const pxAntes = PX_DIA[escalaPrev.current];
    const diasCentro = (el.scrollLeft + el.clientWidth / 2) / pxAntes;
    escalaPrev.current = escala;
    // Tras el repintado con la escala nueva
    requestAnimationFrame(() => {
      el.scrollLeft = diasCentro * PX_DIA[escala] - el.clientWidth / 2;
    });
  }, [escala]);

  // La escala que se recuerda es GLOBAL, y eso esta bien para una preferencia
  // —quien trabaja por semanas quiere semanas— pero no puede convertir un
  // proyecto de seis dias en una astilla de 22 px. Si la recordada lo aplasta,
  // este proyecto se abre en la que si cabe.
  //
  // No se guarda: es una correccion para ESTE proyecto, no una eleccion. Si se
  // guardara, abrir un proyecto corto le cambiaria la preferencia a quien la
  // tenia puesta a proposito para los largos.
  const ajustada = useRef(false);
  useEffect(() => {
    if (ajustada.current || !iso0 || !iso1) return;
    ajustada.current = true;
    if (alcanzaElMinimo(iso0, iso1, escala)) return;
    // Y solo se cambia si hay algo MEJOR. Un proyecto de seis dias no llega al
    // minimo ni por dias, y cambiarlo a otra escala solo lo empeoraria.
    const mejor = escalaQueCabe(iso0, iso1, anchoVis);
    if (mejor !== escala) { escalaPrev.current = mejor; setEscala(mejor); }
  }, [iso0, iso1, anchoVis]); // eslint-disable-line

  // Al abrir, centrar en hoy si cae en la ventana
  const centrado = useRef(false);
  useEffect(() => {
    const el = cuerpoRef.current;
    if (!el || centrado.current || !regla.ancho) return;
    centrado.current = true;
    const x = xDe(hoy, iso0, px);
    if (x > 0 && x < regla.ancho) el.scrollLeft = Math.max(0, x - el.clientWidth / 2);
  }, [regla.ancho, hoy, iso0, px]);

  // Solo se recuerda lo que una persona elige. El ajuste automatico de arriba no
  // pasa por aqui a proposito.
  const elegirEscala = (k) => {
    setEscala(k);
    try { localStorage.setItem(CLAVE_ESCALA, k); } catch { /* privado */ }
  };

  const xHoy = xDe(hoy, iso0, px);
  const hoyVisible = xHoy >= 0 && xHoy <= regla.ancho;
  const altoTotal = Math.max(filas.length * altoF, 40);

  return (
    <div className="cr-wrap">
      {/* Barra de escala */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 10px",
        borderBottom: "1px solid var(--border)", flexShrink: 0, flexWrap: "wrap" }}>
        {ESCALAS.map(e => (
          <button key={e.key} className="chip btn" onClick={() => elegirEscala(e.key)}
            style={{ padding: "4px 11px", borderRadius: 20, cursor: "pointer", fontSize: 11.5,
              fontFamily: "var(--font)", display: "flex", alignItems: "center", gap: 5,
              fontWeight: escala === e.key ? 700 : 500,
              border: `1px solid ${escala === e.key ? "var(--accent)" : "var(--border)"}`,
              background: escala === e.key ? "var(--accent-bg)" : "var(--surface)",
              color: escala === e.key ? "var(--accent)" : "var(--text2)" }}>
            <Icon name={e.icon} size={11} color={escala === e.key ? "var(--accent)" : "var(--text3)"} />
            {e.label}
          </button>
        ))}
        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center",
          fontSize: 10, color: "var(--text3)" }}>
          <Leyenda color="var(--accent)" texto="Hoy" trazo />
          <Leyenda color="#9ca3af" texto="Previsto" />
          <Leyenda color="var(--red)" texto="Retraso" trazo />
          {demasiados && <span>· conectores solo en la fila activa</span>}
        </div>
      </div>

      {/* Avisos honestos sobre el tiempo real */}
      {tiempoError && (
        <div style={{ background: "var(--warn-bg)", borderBottom: "1px solid var(--warn-line)", color: "var(--warn-text)",
          padding: "7px 11px", fontSize: 11, lineHeight: 1.5, flexShrink: 0 }}>
          No se ha podido leer el tiempo real del cuaderno. Las barras se pintan solo con su
          planificacion: donde no hay relleno no significa cero horas, significa que no hay dato.
        </div>
      )}
      {tiempoParcial && (
        <div style={{ background: "var(--surface2)", borderBottom: "1px solid var(--border)",
          color: "var(--text3)", padding: "6px 11px", fontSize: 10.5, flexShrink: 0 }}>
          El tiempo que ves es solo el tuyo: el del resto del equipo es de administracion.
        </div>
      )}

      {/* Regla */}
      <div className="cr-head">
        <div style={{ width: ANCHO_NOMBRES, flexShrink: 0, borderRight: "1px solid var(--border)",
          display: "flex", alignItems: "flex-end", padding: "0 10px 4px",
          fontSize: 9, fontWeight: 700, textTransform: "uppercase",
          letterSpacing: ".07em", color: "var(--text3)" }}>
          Paso
        </div>
        <div ref={reglaRef} className="cr-scroll-head">
          <svg width={regla.ancho} height={40} style={{ display: "block" }}>
            {/* Piso superior: mes o año */}
            {regla.grupos.map(g => (
              <g key={g.clave}>
                <line x1={g.x} y1={0} x2={g.x} y2={40} className="cr-grid" />
                <text className="cr-rule" x={g.x + Math.min(g.w, anchoVis) / 2} y={13}
                  style={{ fontWeight: 700, fill: "var(--text2)" }}>
                  {g.texto}
                </text>
              </g>
            ))}
            {/* Piso inferior: dia, semana o mes */}
            {regla.dias.map(d => (
              <g key={d.iso}>
                {escala === "dia" && d.finde && (
                  <rect x={d.x} y={20} width={d.w} height={20} className="cr-nolab" />
                )}
                <text className="cr-rule" x={d.x + d.w / 2} y={33}>{d.texto}</text>
              </g>
            ))}
            {hoyVisible && (
              <g>
                <line x1={xHoy} y1={18} x2={xHoy} y2={40} className="cr-hoy" />
                <rect x={xHoy - 20} y={18} width={40} height={13} rx={6} fill="var(--accent)" />
                <text x={xHoy} y={27.5} textAnchor="middle"
                  style={{ fontSize: 8.5, fontWeight: 700, fill: "#fff",
                    fontFamily: "var(--mono)" }}>HOY</text>
              </g>
            )}
          </svg>
        </div>
      </div>

      {/* Cuerpo */}
      <div className="cr-body">
        <div className="cr-names" style={{ width: ANCHO_NOMBRES }}>
          <div style={{ height: altoTotal }}>
            {filas.map((t, i) => (
              <div key={t.id} className={"cr-row" + (filaActiva === String(t.id) ? " cr-on" : "")}
                style={{ height: altoF }}
                role="button" tabIndex={0} aria-label={t.title}
                onMouseEnter={() => setFilaActiva(String(t.id))}
                onMouseLeave={() => setFilaActiva(null)}
                onClick={() => onAbrirPaso?.(t)}
                onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onAbrirPaso?.(t); } }}
                title={t.title}>
                {t.kind === "hito"
                  ? <Icon name="diamond-fill" size={9} color={PR_CLR[t.priority] || "#6b7280"} />
                  : <span style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
                      background: ST_CLR[t.status] || "#6b7280" }} />}
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis",
                  whiteSpace: "nowrap", fontWeight: t.kind === "hito" ? 700 : 400 }}>
                  {t.title}
                </span>
                {(t.asignados || []).slice(0, 2).map((a, j) => (
                  <div key={a.user_id + a.rol} style={{ marginLeft: j ? -5 : 0, flexShrink: 0 }}
                    title={`${a.name} · ${a.rol}`}>
                    <Avatar name={a.name} size={17}
                      color={a.rol === "encargado" ? "var(--accent)" : "var(--text3)"} />
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div ref={cuerpoRef} className="cr-scroll" onScroll={alScroll}>
          <svg width={regla.ancho} height={altoTotal} style={{ display: "block" }}>
            {/* Bandas de no laborables. A escala de mes no se pintan: a 3,6 px
                por dia son rayas de moire. */}
            {escala !== "mes" && bandas.map((b, i) => (
              <rect key={i} x={b.x} y={0} width={b.w} height={altoTotal}
                className="cr-nolab" opacity={escala === "semana" ? 0.5 : 1} />
            ))}

            {/* Rejilla vertical */}
            {regla.dias.map(d => (
              <line key={d.iso} x1={d.x} y1={0} x2={d.x} y2={altoTotal} className="cr-grid" />
            ))}

            {/* Separadores de fila */}
            {filas.map((t, i) => (
              <line key={"h" + t.id} x1={0} y1={(i + 1) * altoF} x2={regla.ancho}
                y2={(i + 1) * altoF} stroke="var(--border2)" strokeWidth={1} />
            ))}

            {/* Conectores, DEBAJO de las barras */}
            {conectores.map(c => {
              if (demasiados && filaActiva !== c.from && filaActiva !== c.to) return null;
              return (
                <path key={c.id} d={c.d} className={"cr-dep" + (c.mal ? " cr-mal" : "")} />
              );
            })}

            {/* Barras */}
            {filas.map((t, i) => {
              const b = barras[String(t.id)];
              if (!b) return null;
              const y = i * altoF;
              const clr = ST_CLR[t.status] || "#6b7280";
              const activa = filaActiva === String(t.id);

              if (t.kind === "hito") {
                const cx = b.x + b.w / 2, cy = y + altoF / 2;
                const vencido = b.vencida;
                return (
                  <g key={t.id} className="cr-bar" role="button" tabIndex={0} aria-label={t.title}
                    onClick={() => onAbrirPaso?.(t)}
                    onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onAbrirPaso?.(t); } }}
                    onMouseEnter={() => setFilaActiva(String(t.id))}
                    onMouseLeave={() => setFilaActiva(null)}>
                    <title>{`${t.title} · hito${t.due_date ? " · " + t.due_date : ""}${vencido ? " · vencido" : ""}`}</title>
                    <rect x={cx - 5.5} y={cy - 5.5} width={11} height={11} rx={2}
                      transform={`rotate(45 ${cx} ${cy})`}
                      fill={vencido ? "var(--red)" : (PR_CLR[t.priority] || clr)}
                      stroke={vencido ? "var(--red)" : "none"} strokeWidth={2} />
                    <text x={cx + 11} y={cy + 3.5} className="cr-rule"
                      textAnchor="start" style={{ fill: "var(--text2)", fontWeight: 700 }}>
                      {t.title.length > 22 ? t.title.slice(0, 21) + "…" : t.title}
                    </text>
                  </g>
                );
              }

              const alto = Math.min(16, altoF - 12);
              const yB = y + (altoF - alto) / 2;
              const idClip = `cr-clip-${t.id}`;
              return (
                <g key={t.id} className="cr-bar" role="button" tabIndex={0} aria-label={t.title}
                  onClick={() => onAbrirPaso?.(t)}
                  onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onAbrirPaso?.(t); } }}
                  onMouseEnter={() => setFilaActiva(String(t.id))}
                  onMouseLeave={() => setFilaActiva(null)}>
                  <title>
                    {[t.title,
                      `previsto ${b.inicio} → ${b.fin}`,
                      b.est ? `estimado ${fmtHours(b.est / 60)}` : "sin estimacion",
                      b.real ? `real ${fmtHours(b.real / 60)}` : "sin horas registradas",
                      b.exceso ? `+${Math.round(b.exceso * 100)}% sobre lo estimado` : null,
                      b.vencida ? "vencida" : null,
                      b.fuente !== "horas" ? `avance estimado por ${b.fuente}` : null,
                    ].filter(Boolean).join(" · ")}
                  </title>

                  <defs>
                    <clipPath id={idClip}>
                      <rect x={b.x} y={yB} width={b.w} height={alto} rx={5} />
                    </clipPath>
                  </defs>

                  {/* Cola de retraso: hasta hoy, punteada */}
                  {b.vencida && b.colaW > 0 && (
                    <rect x={b.x + b.w} y={yB + 2} width={b.colaW} height={alto - 4} rx={4}
                      fill="none" stroke="var(--red)" strokeWidth={1.5} strokeDasharray="4 3" />
                  )}

                  {/* Contorno de lo previsto */}
                  <rect x={b.x} y={yB} width={b.w} height={alto} rx={5}
                    fill={clr + "22"} stroke={clr} strokeWidth={activa ? 1.6 : 1} />

                  {/* Relleno de avance. Con borde punteado si no viene de horas:
                      no se finge una precision que no hay. */}
                  {b.avance > 0 && (
                    <rect x={b.x} y={yB} width={b.w * b.avance} height={alto} rx={5}
                      fill={clr} clipPath={`url(#${idClip})`}
                      opacity={b.fuente === "horas" ? 1 : 0.55} />
                  )}

                  {/* Sobrecoste: SOBRESALE por la derecha a proposito. El mensaje
                      es "se ha comido mas de lo que tenia". */}
                  {b.exceso > 0 && (
                    <>
                      <rect x={b.x + b.w} y={yB + 1} width={Math.min(b.w, b.w * b.exceso)}
                        height={alto - 2} rx={4} fill="url(#cr-hatch)" />
                      <text x={b.x + b.w + Math.min(b.w, b.w * b.exceso) + 4} y={yB + alto - 4}
                        className="cr-rule" textAnchor="start"
                        style={{ fill: "var(--red)", fontWeight: 700 }}>
                        +{Math.round(b.exceso * 100)}%
                      </text>
                    </>
                  )}

                  {/* Tira de tiempo real por dia: es lo unico que responde a
                      "¿cuando se trabajo de verdad?", y cuesta 4 px de fila en
                      vez de una segunda barra de 26. */}
                  {(porDiaPorTarea[String(t.id)] || []).map(d => {
                    const x = xDe(d.fecha, iso0, px);
                    if (x < 0 || x > regla.ancho) return null;
                    const h = Math.min(4, 1 + (d.minutes / 480) * 3);
                    return (
                      <rect key={d.fecha} x={x} y={y + altoF - 4 - h}
                        width={Math.max(1.5, px * 0.7)} height={h} rx={0.8}
                        fill="#8b5cf6" opacity={0.75} />
                    );
                  })}

                  {/* Chip de avance aproximado */}
                  {b.fuente !== "horas" && b.avance > 0 && (
                    <text x={b.x + 4} y={yB + alto / 2 + 3} className="cr-rule"
                      textAnchor="start" style={{ fill: clr, fontWeight: 700 }}
                      >≈</text>
                  )}
                </g>
              );
            })}

            {/* Marca de hoy, por encima de todo */}
            {hoyVisible && (
              <line x1={xHoy} y1={0} x2={xHoy} y2={altoTotal} className="cr-hoy" />
            )}

            <defs>
              {/* Trama del sobrecoste */}
              <pattern id="cr-hatch" width={6} height={6} patternUnits="userSpaceOnUse"
                patternTransform="rotate(45)">
                <rect width={6} height={6} fill="var(--red)" opacity={0.1} />
                <line x1={0} y1={0} x2={0} y2={6} stroke="var(--red)" strokeWidth={2}
                  opacity={0.35} />
              </pattern>
            </defs>
          </svg>
        </div>
      </div>

      {/* Lo que no se puede pintar: se dice, no se esconde */}
      {!!sinFechas.length && (
        <div style={{ borderTop: "1px solid var(--border)", padding: "8px 11px",
          background: "var(--surface2)", flexShrink: 0, maxHeight: 120, overflowY: "auto" }}>
          <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase",
            letterSpacing: ".07em", color: "var(--text3)", marginBottom: 5 }}>
            Sin planificar ({sinFechas.length})
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {sinFechas.map(t => (
              <button key={t.id} className="btn" onClick={() => onAbrirPaso?.(t)}
                title="Ponerle fechas para que aparezca en la cronologia"
                style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 9px",
                  borderRadius: 20, cursor: "pointer", fontSize: 11, fontFamily: "var(--font)",
                  border: "1px dashed var(--border)", background: "var(--surface)",
                  color: "var(--text2)" }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%",
                  background: ST_CLR[t.status] || "#6b7280" }} />
                {t.title}
              </button>
            ))}
          </div>
        </div>
      )}

      {!filas.length && !sinFechas.length && (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ textAlign: "center", maxWidth: 320 }}>
            <Icon name="calendar-week" size={28} color="var(--text3)" />
            <div style={{ fontWeight: 700, fontSize: 14, marginTop: 10, color: "var(--text2)" }}>
              Nada que situar en el tiempo
            </div>
            <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 6, lineHeight: 1.6 }}>
              Añade pasos con fecha de inicio y de fin y apareceran aqui, con las horas
              reales del cuaderno superpuestas.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Leyenda = ({ color, texto, trazo }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
    <span style={{ width: 12, height: 0, borderTop: `2px ${trazo ? "dashed" : "solid"} ${color}` }} />
    {texto}
  </span>
);
